import { useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  TodoCard,
  QuestionCard,
  FileCard,
  RememberCard,
} from "./SideBrainCards";
import {
  SideBrainTodo,
  SideBrainQuestion,
  SideBrainFile,
  SideBrainRemember,
} from "@/types/sidebrain";
import { Upload, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { logDatabaseOperation } from "@/lib/logger";

type PanelType =
  | "currentTodo"
  | "stashTodo"
  | "stashQuestion"
  | "stashFile"
  | "quickQuestion"
  | "remember";

interface SortableAccordionItemProps {
  id: string;
  value: string;
  title: string;
  children: React.ReactNode;
  headerContent?: React.ReactNode;
  eyeIcon?: boolean;
  eyeActive?: boolean;
  onEyeClick?: () => void;
}

function SortableAccordionItem({
  id,
  value,
  title,
  children,
  headerContent,
  eyeIcon,
  eyeActive,
  onEyeClick,
}: SortableAccordionItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <AccordionItem value={value} className="border-b">
        <div className="flex items-stretch bg-muted">
          {/* Drag Handle */}
          <div
            {...attributes}
            {...listeners}
            className="flex items-center px-2 cursor-grab active:cursor-grabbing"
            title="Drag to reorder"
          >
            <div className="w-1 h-5 bg-muted-foreground/20 rounded" />
          </div>

          {/* Accordion Trigger - Chevron on the left */}
          <AccordionTrigger className="w-10 hover:no-underline justify-center px-0" />

          {/* Content area that grows */}
          <div className="flex-1 flex items-center gap-3 min-w-0 py-4">
            <span className="font-semibold text-base whitespace-nowrap">
              {title}
            </span>
            {headerContent && (
              <div className="flex-1 min-w-0">{headerContent}</div>
            )}
          </div>

          {/* Eye Icon - Fixed position on right */}
          <div className="flex items-center w-10 justify-center pr-4">
            {eyeIcon && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={(e) => {
                  e.stopPropagation();
                  onEyeClick?.();
                }}
                title={eyeActive ? "Hide stashed items" : "Show stashed items"}
              >
                <Eye
                  className={`h-4 w-4 ${
                    eyeActive ? "text-primary" : "text-muted-foreground"
                  }`}
                />
              </Button>
            )}
          </div>
        </div>

        <AccordionContent>{children}</AccordionContent>
      </AccordionItem>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="text-center text-muted-foreground py-8">{message}</div>
  );
}

function StashHiddenState({ count }: { count: number }) {
  return (
    <div className="text-center py-8">
      <Eye className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
      <p className="text-sm text-muted-foreground">
        {count > 0 ? "Items stashed" : "Stash is empty"}
      </p>
      <p className="text-xs text-muted-foreground">Click eye to reveal</p>
    </div>
  );
}

export function SideBrainContainer() {
  const [panelOrder] = useState<PanelType[]>([
    "currentTodo",
    "stashTodo",
    "stashQuestion",
    "stashFile",
    "quickQuestion",
    "remember",
  ]);

  const [openPanels, setOpenPanels] = useState<string[]>([
    "currentTodo",
    "quickQuestion",
    "remember",
  ]);

  const [revealedStashes, setRevealedStashes] = useState({
    stashTodo: false,
    stashQuestion: false,
    stashFile: false,
  });

  const [currentTodos, setCurrentTodos] = useState<SideBrainTodo[]>([]);
  const [todos, setTodos] = useState<SideBrainTodo[]>([]);
  const [stashQuestions, setStashQuestions] = useState<SideBrainQuestion[]>([]);
  const [files, setFiles] = useState<SideBrainFile[]>([]);
  const [quickQuestions, setQuickQuestions] = useState<SideBrainQuestion[]>([]);
  const [remembers, setRemembers] = useState<SideBrainRemember[]>([]);

  const [inputs, setInputs] = useState({
    currentTodo: "",
    stashTodo: "",
    stashQuestion: "",
    quickQuestion: "",
    remember: "",
  });

  // Load data from database on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load current todos
        const { data: currentTodosData, error: currentTodosError } =
          await supabase
            .from("sidebrain_current_todos")
            .select("*")
            .eq("deleted", false)
            .order("order_index", { ascending: true });

        if (currentTodosError) throw currentTodosError;

        setCurrentTodos(
          (currentTodosData || []).map((row) => ({
            id: row.id,
            user_id: row.user_id,
            content: row.content,
            completed: row.completed || false,
            created_at: row.created_at || new Date().toISOString(),
            deleted: row.deleted || false,
            order_index: row.order_index || 0,
          }))
        );

        logDatabaseOperation("select", "sidebrain_current_todos", true);

        // Load stash todos
        const { data: stashTodosData, error: stashTodosError } = await supabase
          .from("sidebrain_stash_todos")
          .select("*")
          .eq("deleted", false)
          .order("order_index", { ascending: true });

        if (stashTodosError) throw stashTodosError;

        setTodos(
          (stashTodosData || []).map((row) => ({
            id: row.id,
            user_id: row.user_id,
            content: row.content,
            completed: row.completed || false,
            created_at: row.created_at || new Date().toISOString(),
            deleted: row.deleted || false,
            order_index: row.order_index || 0,
          }))
        );

        logDatabaseOperation("select", "sidebrain_stash_todos", true);

        // Load quick questions
        const { data: quickQuestionsData, error: quickQuestionsError } =
          await supabase
            .from("sidebrain_quick_questions")
            .select("*")
            .eq("deleted", false)
            .order("order_index", { ascending: true });

        if (quickQuestionsError) throw quickQuestionsError;

        setQuickQuestions(
          (quickQuestionsData || []).map((row) => ({
            id: row.id,
            user_id: row.user_id,
            question: row.question,
            answer: row.answer || undefined,
            answer_summary: row.answer_summary || undefined,
            answered_at: row.answered_at || undefined,
            created_at: row.created_at || new Date().toISOString(),
            deleted: row.deleted || false,
            order_index: row.order_index || 0,
          }))
        );

        logDatabaseOperation("select", "sidebrain_quick_questions", true);

        // Load stash questions
        const { data: stashQuestionsData, error: stashQuestionsError } =
          await supabase
            .from("sidebrain_stash_questions")
            .select("*")
            .eq("deleted", false)
            .order("order_index", { ascending: true });

        if (stashQuestionsError) throw stashQuestionsError;

        setStashQuestions(
          (stashQuestionsData || []).map((row) => ({
            id: row.id,
            user_id: row.user_id,
            question: row.question,
            answer: row.answer || undefined,
            answer_summary: row.answer_summary || undefined,
            answered_at: row.answered_at || undefined,
            created_at: row.created_at || new Date().toISOString(),
            deleted: row.deleted || false,
            order_index: row.order_index || 0,
          }))
        );

        logDatabaseOperation("select", "sidebrain_stash_questions", true);

        // Load stash files
        const { data: filesData, error: filesError } = await supabase
          .from("sidebrain_stash_files")
          .select("*")
          .eq("deleted", false)
          .order("order_index", { ascending: true });

        if (filesError) throw filesError;

        setFiles(
          (filesData || []).map((row) => ({
            id: row.id,
            user_id: row.user_id,
            file_name: row.file_name,
            file_type: row.file_type,
            file_size: row.file_size,
            storage_path: row.storage_path,
            data_url: row.data_url || undefined,
            created_at: row.created_at || new Date().toISOString(),
            deleted: row.deleted || false,
            order_index: row.order_index || 0,
          }))
        );

        logDatabaseOperation("select", "sidebrain_files", true);

        // Load remembers
        const { data: remembersData, error: remembersError } = await supabase
          .from("sidebrain_remembers")
          .select("*")
          .eq("deleted", false)
          .order("order_index", { ascending: true });

        if (remembersError) throw remembersError;

        setRemembers(
          (remembersData || []).map((row) => ({
            id: row.id,
            user_id: row.user_id,
            statement: row.statement,
            created_at: row.created_at || new Date().toISOString(),
            deleted: row.deleted || false,
            order_index: row.order_index || 0,
          }))
        );

        logDatabaseOperation("select", "sidebrain_remembers", true);
      } catch (error) {
        console.error("Error loading SideBrain data:", error);
        toast.error("Failed to load data");
      }
    };

    loadData();
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      // Panel reordering would go here if needed
    }
  };

  const toggleStashReveal = (stash: keyof typeof revealedStashes) => {
    setRevealedStashes((prev) => ({ ...prev, [stash]: !prev[stash] }));
  };

  const updateInput = (key: keyof typeof inputs, value: string) => {
    setInputs((prev) => ({ ...prev, [key]: value }));
  };

  const clearInput = (key: keyof typeof inputs) => {
    setInputs((prev) => ({ ...prev, [key]: "" }));
  };

  const createTodo = async (content: string, isStash: boolean) => {
    if (!content.trim()) return;

    try {
      let {
        data: { user },
      } = await supabase.auth.getUser();

      // If no user, sign in anonymously (guest user)
      if (!user) {
        const { data: anonData, error: anonError } =
          await supabase.auth.signInAnonymously();

        if (anonError || !anonData?.user) {
          console.error("Failed to sign in anonymously:", anonError);
          toast.error("Authentication failed. Please refresh the page.");
          return;
        }

        user = anonData.user;
      }

      const tableName = isStash
        ? "sidebrain_stash_todos"
        : "sidebrain_current_todos";

      const { data, error } = await supabase
        .from(tableName)
        .insert({
          content,
          completed: false,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;

      const newTodo: SideBrainTodo = {
        id: data.id,
        user_id: data.user_id,
        content: data.content,
        completed: data.completed || false,
        created_at: data.created_at || new Date().toISOString(),
        deleted: data.deleted || false,
        order_index: data.order_index || 0,
      };

      if (isStash) {
        setTodos((prev) => [...prev, newTodo]);
        toast.success("TODO saved to stash");
      } else {
        setCurrentTodos((prev) => [...prev, newTodo]);
        toast.success("TODO added");
      }

      logDatabaseOperation("insert", tableName, true);
    } catch (error) {
      console.error("Error creating todo:", error);
      toast.error("Failed to create TODO");
      const tableName = isStash
        ? "sidebrain_stash_todos"
        : "sidebrain_current_todos";
      logDatabaseOperation("insert", tableName, false, error);
    }
  };

  const createQuestion = async (question: string, isStash: boolean) => {
    if (!question.trim()) return;

    try {
      let {
        data: { user },
      } = await supabase.auth.getUser();

      // If no user, sign in anonymously (guest user)
      if (!user) {
        const { data: anonData, error: anonError } =
          await supabase.auth.signInAnonymously();

        if (anonError || !anonData?.user) {
          console.error("Failed to sign in anonymously:", anonError);
          toast.error("Authentication failed. Please refresh the page.");
          return;
        }

        user = anonData.user;
      }

      const tableName = isStash
        ? "sidebrain_stash_questions"
        : "sidebrain_quick_questions";

      const { data, error } = await supabase
        .from(tableName)
        .insert({
          question,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;

      const newQuestion: SideBrainQuestion = {
        id: data.id,
        user_id: data.user_id,
        question: data.question,
        answer: data.answer || undefined,
        answer_summary: data.answer_summary || undefined,
        answered_at: data.answered_at || undefined,
        created_at: data.created_at || new Date().toISOString(),
        deleted: data.deleted || false,
        order_index: data.order_index || 0,
      };

      if (isStash) {
        setStashQuestions((prev) => [...prev, newQuestion]);
        toast.success("Question saved to stash");
      } else {
        setQuickQuestions((prev) => [...prev, newQuestion]);
      }

      logDatabaseOperation("insert", tableName, true);
    } catch (error) {
      console.error("Error creating question:", error);
      toast.error("Failed to create question");
      const tableName = isStash
        ? "sidebrain_stash_questions"
        : "sidebrain_quick_questions";
      logDatabaseOperation("insert", tableName, false, error);
    }
  };

  const createRemember = async (statement: string) => {
    if (!statement.trim()) return;

    try {
      let {
        data: { user },
      } = await supabase.auth.getUser();

      // If no user, sign in anonymously (guest user)
      if (!user) {
        const { data: anonData, error: anonError } =
          await supabase.auth.signInAnonymously();

        if (anonError || !anonData?.user) {
          console.error("Failed to sign in anonymously:", anonError);
          toast.error("Authentication failed. Please refresh the page.");
          return;
        }

        user = anonData.user;
      }

      const { data, error } = await supabase
        .from("sidebrain_remembers")
        .insert({
          statement,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;

      const newRemember: SideBrainRemember = {
        id: data.id,
        user_id: data.user_id,
        statement: data.statement,
        created_at: data.created_at || new Date().toISOString(),
        deleted: data.deleted || false,
        order_index: data.order_index || 0,
      };

      setRemembers((prev) => [...prev, newRemember]);
      logDatabaseOperation("insert", "sidebrain_remembers", true);
    } catch (error) {
      console.error("Error creating remember:", error);
      toast.error("Failed to save remember");
      logDatabaseOperation("insert", "sidebrain_remembers", false, error);
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      let {
        data: { user },
      } = await supabase.auth.getUser();

      // If no user, sign in anonymously (guest user)
      if (!user) {
        const { data: anonData, error: anonError } =
          await supabase.auth.signInAnonymously();

        if (anonError || !anonData?.user) {
          console.error("Failed to sign in anonymously:", anonError);
          toast.error("Authentication failed. Please refresh the page.");
          return;
        }

        user = anonData.user;
      }

      // Read file as data URL for images
      let dataUrl: string | undefined;
      if (file.type.startsWith("image/")) {
        dataUrl = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        });
      }

      const { data, error } = await supabase
        .from("sidebrain_stash_files")
        .insert({
          file_name: file.name,
          file_type: file.type,
          file_size: file.size,
          storage_path: "", // TODO: Upload to Supabase Storage
          data_url: dataUrl,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;

      const newFile: SideBrainFile = {
        id: data.id,
        user_id: data.user_id,
        file_name: data.file_name,
        file_type: data.file_type,
        file_size: data.file_size,
        storage_path: data.storage_path,
        data_url: data.data_url || undefined,
        created_at: data.created_at || new Date().toISOString(),
        deleted: data.deleted || false,
        order_index: data.order_index || 0,
      };

      setFiles((prev) => [...prev, newFile]);
      toast.success("File saved to stash");
      logDatabaseOperation("insert", "sidebrain_stash_files", true);
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Failed to upload file");
      logDatabaseOperation("insert", "sidebrain_stash_files", false, error);
    }

    // Reset input
    event.target.value = "";
  };

  const toggleTodo = async (id: string, isStash: boolean) => {
    try {
      const todoList = isStash ? todos : currentTodos;
      const todo = todoList.find((t) => t.id === id);
      if (!todo) return;

      const tableName = isStash
        ? "sidebrain_stash_todos"
        : "sidebrain_current_todos";

      const { error } = await supabase
        .from(tableName)
        .update({ completed: !todo.completed })
        .eq("id", id);

      if (error) throw error;

      const setter = isStash ? setTodos : setCurrentTodos;
      setter((prev) =>
        prev.map((todo) =>
          todo.id === id ? { ...todo, completed: !todo.completed } : todo
        )
      );

      logDatabaseOperation("update", tableName, true);
    } catch (error) {
      console.error("Error toggling todo:", error);
      toast.error("Failed to update TODO");
      const tableName = isStash
        ? "sidebrain_stash_todos"
        : "sidebrain_current_todos";
      logDatabaseOperation("update", tableName, false, error);
    }
  };

  const deleteTodo = async (id: string, isStash: boolean) => {
    try {
      const tableName = isStash
        ? "sidebrain_stash_todos"
        : "sidebrain_current_todos";

      const { error } = await supabase
        .from(tableName)
        .update({ deleted: true })
        .eq("id", id);

      if (error) throw error;

      const setter = isStash ? setTodos : setCurrentTodos;
      setter((prev) => prev.filter((todo) => todo.id !== id));

      logDatabaseOperation("update", tableName, true);
    } catch (error) {
      console.error("Error deleting todo:", error);
      toast.error("Failed to delete TODO");
      const tableName = isStash
        ? "sidebrain_stash_todos"
        : "sidebrain_current_todos";
      logDatabaseOperation("update", tableName, false, error);
    }
  };

  const deleteQuestion = async (id: string, isStash: boolean) => {
    try {
      const tableName = isStash
        ? "sidebrain_stash_questions"
        : "sidebrain_quick_questions";

      const { error } = await supabase
        .from(tableName)
        .update({ deleted: true })
        .eq("id", id);

      if (error) throw error;

      const setter = isStash ? setStashQuestions : setQuickQuestions;
      setter((prev) => prev.filter((q) => q.id !== id));

      logDatabaseOperation("update", tableName, true);
    } catch (error) {
      console.error("Error deleting question:", error);
      toast.error("Failed to delete question");
      const tableName = isStash
        ? "sidebrain_stash_questions"
        : "sidebrain_quick_questions";
      logDatabaseOperation("update", tableName, false, error);
    }
  };

  const deleteFile = async (id: string) => {
    try {
      const { error } = await supabase
        .from("sidebrain_stash_files")
        .update({ deleted: true })
        .eq("id", id);

      if (error) throw error;

      setFiles((prev) => prev.filter((f) => f.id !== id));

      logDatabaseOperation("update", "sidebrain_stash_files", true);
    } catch (error) {
      console.error("Error deleting file:", error);
      toast.error("Failed to delete file");
      logDatabaseOperation("update", "sidebrain_stash_files", false, error);
    }
  };

  const deleteRemember = async (id: string) => {
    try {
      const { error } = await supabase
        .from("sidebrain_remembers")
        .update({ deleted: true })
        .eq("id", id);

      if (error) throw error;

      setRemembers((prev) => prev.filter((r) => r.id !== id));

      logDatabaseOperation("update", "sidebrain_remembers", true);
    } catch (error) {
      console.error("Error deleting remember:", error);
      toast.error("Failed to delete remember");
      logDatabaseOperation("update", "sidebrain_remembers", false, error);
    }
  };

  const panelConfigs: Record<PanelType, any> = {
    currentTodo: {
      title: "TODO",
      placeholder: "Add a TODO item...",
      inputKey: "currentTodo",
      isStash: false,
      hasEye: false,
      data: currentTodos,
      emptyMessage: "No TODOs yet",
      onSubmit: () => {
        createTodo(inputs.currentTodo, false);
        clearInput("currentTodo");
      },
      renderItems: () =>
        currentTodos.map((todo) => (
          <TodoCard
            key={todo.id}
            todo={todo}
            onToggle={() => toggleTodo(todo.id, false)}
            onDelete={() => deleteTodo(todo.id, false)}
          />
        )),
    },
    stashTodo: {
      title: "Stash TODO",
      placeholder: "Add a TODO item...",
      inputKey: "stashTodo",
      isStash: true,
      hasEye: true,
      stashKey: "stashTodo",
      data: todos,
      emptyMessage: "No TODOs yet",
      onSubmit: () => {
        createTodo(inputs.stashTodo, true);
        clearInput("stashTodo");
      },
      renderItems: () =>
        todos.map((todo) => (
          <TodoCard
            key={todo.id}
            todo={todo}
            onToggle={() => toggleTodo(todo.id, true)}
            onDelete={() => deleteTodo(todo.id, true)}
          />
        )),
    },
    stashQuestion: {
      title: "Stash Question",
      placeholder: "Ask a question...",
      inputKey: "stashQuestion",
      isStash: true,
      hasEye: true,
      stashKey: "stashQuestion",
      data: stashQuestions,
      emptyMessage: "No questions yet",
      onSubmit: () => {
        createQuestion(inputs.stashQuestion, true);
        clearInput("stashQuestion");
      },
      renderItems: () =>
        stashQuestions.map((q) => (
          <QuestionCard
            key={q.id}
            question={q}
            onDelete={() => deleteQuestion(q.id, true)}
          />
        )),
    },
    stashFile: {
      title: "Stash File",
      isStash: true,
      hasEye: true,
      stashKey: "stashFile",
      data: files,
      emptyMessage: "No files yet",
      renderHeader: () => (
        <label className="flex-1" onClick={(e) => e.stopPropagation()}>
          <input type="file" className="hidden" onChange={handleFileUpload} />
          <Button variant="outline" size="sm" className="w-full h-8" asChild>
            <span className="cursor-pointer">
              <Upload className="h-3 w-3 mr-1" />
              Upload
            </span>
          </Button>
        </label>
      ),
      renderItems: () =>
        files.map((file) => (
          <FileCard
            key={file.id}
            file={file}
            onDelete={() => deleteFile(file.id)}
          />
        )),
    },
    quickQuestion: {
      title: "Quick Question",
      placeholder: "Ask a quick question...",
      inputKey: "quickQuestion",
      isStash: false,
      hasEye: false,
      data: quickQuestions,
      emptyMessage: "No questions yet",
      onSubmit: () => {
        createQuestion(inputs.quickQuestion, false);
        clearInput("quickQuestion");
      },
      renderItems: () =>
        quickQuestions.map((q) => (
          <QuestionCard
            key={q.id}
            question={q}
            onDelete={() => deleteQuestion(q.id, false)}
          />
        )),
    },
    remember: {
      title: "Remember",
      placeholder: "Add something to remember...",
      inputKey: "remember",
      isStash: false,
      hasEye: false,
      data: remembers,
      emptyMessage: "Nothing to remember yet",
      onSubmit: () => {
        createRemember(inputs.remember);
        clearInput("remember");
      },
      renderItems: () =>
        remembers.map((r) => (
          <RememberCard
            key={r.id}
            remember={r}
            onDelete={() => deleteRemember(r.id)}
          />
        )),
    },
  };

  const renderPanel = (panelType: PanelType) => {
    const config = panelConfigs[panelType];
    const isRevealed = config.isStash
      ? revealedStashes[config.stashKey as keyof typeof revealedStashes]
      : true;

    return (
      <SortableAccordionItem
        id={panelType}
        value={panelType}
        title={config.title}
        eyeIcon={config.hasEye}
        eyeActive={isRevealed}
        onEyeClick={
          config.hasEye
            ? () =>
                toggleStashReveal(
                  config.stashKey as keyof typeof revealedStashes
                )
            : undefined
        }
        headerContent={
          config.renderHeader ? (
            config.renderHeader()
          ) : (
            <Input
              placeholder={config.placeholder}
              value={inputs[config.inputKey as keyof typeof inputs] || ""}
              onChange={(e) =>
                updateInput(
                  config.inputKey as keyof typeof inputs,
                  e.target.value
                )
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  config.onSubmit();
                }
                e.stopPropagation();
              }}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 h-8 text-sm"
            />
          )
        }
      >
        <div className="space-y-3 px-4 pb-4">
          {config.isStash && !isRevealed ? (
            <StashHiddenState count={config.data.length} />
          ) : config.data.length === 0 ? (
            <EmptyState message={config.emptyMessage} />
          ) : (
            config.renderItems()
          )}
        </div>
      </SortableAccordionItem>
    );
  };

  return (
    <div className="flex flex-col h-full overflow-hidden min-w-0">
      <div className="border-b px-4 py-3 shrink-0 flex items-center justify-between">
        <h2 className="text-lg font-semibold truncate">SideBrain</h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setOpenPanels([])}
          className="text-xs"
        >
          Collapse All
        </Button>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className="flex-1 min-h-0 overflow-y-auto">
          <Accordion
            type="multiple"
            value={openPanels}
            onValueChange={setOpenPanels}
          >
            <SortableContext
              items={panelOrder}
              strategy={verticalListSortingStrategy}
            >
              {panelOrder.map((panelType) => (
                <div key={panelType}>{renderPanel(panelType)}</div>
              ))}
            </SortableContext>
          </Accordion>
        </div>
      </DndContext>
    </div>
  );
}
